import React, { useState, useEffect } from 'react';

import BigIntegerTextField from '../component/BigIntegerTextField';
import FieldSetFormControl from '../component/FieldSetFormControl';
import LegendFormLabel from '../component/LegendFormLabel';

import Box from "@material-ui/core/Box";
import Checkbox from "@material-ui/core/Checkbox";
import Divider from "@material-ui/core/Divider";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormGroup from "@material-ui/core/FormGroup";
import RadioGroup from "@material-ui/core/RadioGroup";
import Radio from "@material-ui/core/Radio";

import BN from 'bn.js';
import isPrime from 'is-prime';

const curves = {
  "bls12-381": {
    name: 'BLS12-381',
    fieldSize: '0x73EDA753299D7D483339D80809A1D80553BDA402FFFE5BFEFFFFFFFF00000001'},
  "bn-254": {
    name: 'BN-254',
    fieldSize: '0x30644E72E131A029B85045B68181585D2833E84879B9709143E1F593F0000001'},
  "ristretto": {
    name: 'Ristretto',
    fieldSize: '0x1000000000000000000000000000000014DEF9DEA2F79CD65812631A5CF5D3ED'},
  "babyjub": {
    name: 'BabyJub',
    fieldSize: '0x60C89CE5C263405370A08B6D0302B0BAB3EEDB83920EE0A677297DC392126F1'},
  custom: {
    name: 'Custom',
    fieldSize: ''}
};

function toBN (x) {
  x = x.trim ().toLowerCase ();
  if (!x) throw new Error ("Value is empty");
  return x.startsWith ('0x') ? new BN (x.substring (2), 16) :
      x.startsWith ('0b') ? new BN (x.substring (2), 2) :
      new BN (x);
}

function toNumber (x) {
  x = x.trim ();
  if (!x) throw new Error ("Value is empty");
  const xNumber = Number (x.trim ());
  if (Number.isNaN (xNumber)) throw new Error ("Value is invalid");
  return xNumber;
}

function calculateRecommendedD (fieldSize) {
  try {
    const fieldSizeBN = toBN (fieldSize);
    if (fieldSizeBN.lte (new BN (1))) return '';
    var d = 1;
    while (!isPrime (d) || fieldSizeBN.mod (new BN (d)).eq (new BN (1))) d += 1;
    return d.toString ();
  } catch (error) {
    return '';
  }
}

function calculateCapacity (securityLevel, fieldSize) {
  try {
    const securityLevelBN = toBN (securityLevel);
    const fieldSizeBN = toBN (fieldSize);
    if (fieldSizeBN.lte (new BN (1))) return '';
    return BN.max (
      new BN (1),
      new BN (2).mul (
        securityLevelBN).div (
          new BN (
            Math.max (
              1,
              fieldSizeBN.bitLength () - 8)))).toString ();
  } catch (error) {
    return '';
  }
}

function calculateWidth (mode, capacity, output) {
  try {
    const capacityBN = toBN (capacity);
    const outputBN = mode.startsWith ('merkle') ? toBN (output) : capacityBN;
    if (outputBN.lt (capacityBN)) return '';
    switch (mode) {
    case 'merkle2':
      return capacityBN.add (new BN (2).mul (outputBN)).toString (10);
    case 'merkle4':
      return capacityBN.add (new BN (4).mul (outputBN)).toString (10);
    case 'encryption':
    case 'variable':
    case 'constant':
      return capacityBN.add (new BN (4)).toString (10);
    default:
      return '';
    }
  } catch (error) {
    return '';
  }
}

function calculateRounds (sBox, d, fieldSize, securityLevel, width) {
  try {
    const margin = 1.075;
    const rf = 8;
    const dNumber = toNumber (d);
    const fieldSizeLog2 = toBN (fieldSize).bitLength ();
    const securityLevelNumber = toNumber (securityLevel.trim ());
    const widthNumber = toNumber (width.trim ());
    switch (sBox) {
    case 'inv':
      return Math.ceil (rf + margin * (0.5 * Math.min (fieldSizeLog2, securityLevelNumber) + Math.log2 (widthNumber) * (1.0 - rf))).toString ();
    case 'pow':
      return Math.ceil (rf + margin * (1.0 / Math.log2 (dNumber) * Math.min (fieldSizeLog2, securityLevelNumber) + Math.log2 (widthNumber) - rf)).toString ();
    default:
      return '';
    }
  } catch (error) {
    return '';
  }
}

function ParametersCalculator (props) {
  const {
    ...other
  } = props;

  const [ curve, setCurve ] = useState ('');
  const [ fieldSize, setFieldSize ] = useState ('');
  const [ autoSBox, setAutoSBox ] = useState (true);
  const [ sBox, setSBox ] = useState ('');
  const [ d, setD ] = useState ('');
  const [ securityLevel, setSecurityLevel ] = useState ('');
  const [ mode, setMode ] = useState ('');
  const [ capacity, setCapacity ] = useState ('');
  const [ output, setOutput ] = useState ('');
  const [ outputBits, setOutputBits ] = useState ('');
  const [ width, setWidth ] = useState ('');
  const [ rounds, setRounds ] = useState ('');

  useEffect (() => {
    if (curve)
      setFieldSize (curves[curve].fieldSize);
  }, [ curve ]);

  useEffect (() => {
    if (autoSBox) {
      setSBox ('pow');
      setD (calculateRecommendedD (fieldSize));
    }
  }, [ fieldSize, autoSBox ]);

  useEffect (() => {
    try {
      setOutputBits (new BN (toBN (fieldSize).sub (new BN (1)).bitLength ()).mul (toBN (output)).toString ());
    } catch (error) {
      setOutputBits ('');
    }
  }, [ fieldSize, output ]);

  useEffect (() => {
    setCapacity (calculateCapacity (securityLevel, fieldSize));
  }, [ securityLevel, fieldSize ]);

  useEffect (() => {
    setWidth (calculateWidth (mode, capacity, output));
  }, [ mode, capacity, output ]);

  useEffect (() => {
    setRounds (calculateRounds (sBox, d, fieldSize, securityLevel, width));
  }, [ sBox, d, fieldSize, securityLevel, width] );

  return (
    <Box my={-1}
         component={FieldSetFormControl}
         fullWidth
         {...other}>
      <RadioGroup row
                  value={curve}
                  onChange={event => setCurve (event.target.value)}>
        <Box alignSelf="center"
             mr={2}
             component={LegendFormLabel}>
          Field:
        </Box>
        {Object.entries (curves).map (([key, {name, modulo}]) =>
          <FormControlLabel key={key}
                            label={name}
                            control={<Radio />}
                            value={key} />
        )}
      </RadioGroup>
      <Box mt={1}
           component={BigIntegerTextField}
           readOnly={curve !== 'custom'}
           label="Size"
           value={fieldSize}
           validator={bn => bn.lte (new BN (1)) ? 'Value too low' : undefined}
           onChange={event => setFieldSize (event.target.value)} />
      <RadioGroup row
                  value={sBox}
                  onChange={event => setSBox (event.target.value)}>
        <Box alignSelf="center"
             mr={2}
             component={LegendFormLabel}>
          S-Box:
        </Box>
        <FormControlLabel
             label="Auto"
             control={
               <Checkbox disabled={mode === 'decode'}
                         checked={autoSBox}
                         onChange={event => setAutoSBox (event.target.checked)}/>
             } />
        <FormControlLabel
             label="1 / x"
             control={<Radio disabled={autoSBox} />}
             value="inv" />
        <FormControlLabel
             label="x ^ D"
             control={<Radio disabled={autoSBox} />}
             value="pow" />
      </RadioGroup>
      <Box mt={1}
           component={BigIntegerTextField}
           readOnly={autoSBox}
           label="D"
           value={d}
           disabled={sBox !== 'pow'}
           onChange={event => setD (event.target.value)} />
      <RadioGroup row
                  value={securityLevel}
                  onChange={event => setSecurityLevel (event.target.value)}>
        <Box alignSelf="center"
             mr={2}
             component={LegendFormLabel}>
          Security level:
        </Box>
        <FormControlLabel label="80"
                          control={<Radio />}
                          value="80" />
        <FormControlLabel label="128"
                          control={<Radio />}
                          value="128" />
        <FormControlLabel label="256"
                          control={<Radio />}
                          value="256" />
      </RadioGroup>
      <RadioGroup row
                  value={mode}
                  onChange={event => setMode (event.target.value)}>
        <Box alignSelf="center"
             mr={2}
             component={LegendFormLabel}>
          Mode:
        </Box>
        <FormControlLabel label="Merkle tree 2:1"
                          control={<Radio />}
                          value="merkle2" />
        <FormControlLabel label="Merkle tree 4:1"
                          control={<Radio />}
                          value="merkle4" />
        <FormControlLabel label="Encryption"
                          control={<Radio />}
                          value="encryption" />
        <FormControlLabel label="Variable length"
                          control={<Radio />}
                          value="variable" />
        <FormControlLabel label="Constant length"
                          control={<Radio />}
                          value="constant" />
      </RadioGroup>
      <Box mx={-1}
           component={FormGroup}
           row>
        <Box m={1}
             flexGrow={1}
             component={BigIntegerTextField}
             label="Output"
             disabled={!mode.startsWith ('merkle')}
             value={output}
             validator={bn => capacity && toBN (output).lt (toBN (capacity)) ? 'Value too low' : undefined}
             onChange={event => setOutput (event.target.value)} />
        <Box m={1}
             component={BigIntegerTextField}
             label="Output bits"
             readOnly
             disabled={!mode.startsWith ('merkle')}
             value={outputBits} />
      </Box>
      <Box mt={1}
           mb={3}>
        <Divider />
      </Box>
      <Box mx={-1}
           component={FormGroup}
           row>
        <Box m={1}
             flexGrow={1}
             component={BigIntegerTextField}
             label="Capacity"
             readOnly
             value={capacity} />
        <Box m={1}
             flexGrow={1}
             component={BigIntegerTextField}
             label="Width"
             readOnly
             value={width} />
        <Box m={1}
             flexGrow={1}
             component={BigIntegerTextField}
             label="Rounds"
             readOnly
             value={rounds} />
      </Box>
    </Box>
  );
}

export default ParametersCalculator;
